import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  Modal,
  Container,
  Tooltip,
  TableContainer,
  Box,
  TableHead,
  TableRow,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const GetTimeTab = () => {
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState({ timeTableId: null });
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [subjects, setSubjects] = useState([]); // Initialize as empty array
  const [teachers, setTeachers] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [newSubject, setNewSubject] = useState("");
  const [newTeacher, setNewTeacher] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [selectedTimeTableId, setSelectedTimeTableId] = useState("");
  const [selectedStartTime, setSelectedStartTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");
  const [selectedRoomNo, setSelectedRoomNo] = useState("");
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState("");
  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });

      const result = await response.json();
      const data = result[0];

      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  const handleAddTime = () => {
    navigate("/addtimetable");
  };

  const handleSubjectCellClick = (row, dayName, startTime, endTime, dayOfWeek) => {
    const teacherId = row.teacherIds[dayName] || "";
    setSelectedTeacherId(teacherId);
    const subjectId = row.subjectIds[dayName] || "";
    const timeTableId = row.timeTableIds[dayName] || "";
    const dayofWeekId = row.dayofWeekIds[dayName] || "";
    setSelectedTimeTableId(timeTableId);
    setSelectedStartTime(startTime);
    setSelectedEndTime(endTime);
    setSelectedDayOfWeek(dayofWeekId);
    const roomNo = row.roomNos[dayName] || "";
    setSelectedRoomNo(roomNo);

    setEditingRow({
      ...row,
      className: classes.find((cls) => cls.classId === classId)?.className || "",
      sectionName: sections.find((sec) => sec.sectionId === sectionId)?.sectionName || "",
    });

    setNewSubject(subjectId || "");
    setNewTeacher(teacherId);

    if (classId && sectionId && teacherId) {
      fetchSubjects(teacherId);
    }

    setOpenEditDialog(true);
    setIsEditing(false);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setIsEditing(false);
    setNewSubject("");
    setNewTeacher("");
    setSubjects([]); // Reset subjects when closing
  };

  const handleUpdate = async () => {
    const updatedData = {
      subjectId: newSubject,
      teacherId: newTeacher,
      classId,
      sectionId,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      dayOfWeekId: selectedDayOfWeek,
      objTime: `${selectedStartTime}-${selectedEndTime}`,
      roomNo: parseInt(selectedRoomNo, 10) || 0,
    };

    try {
      const response = await fetch(`https://wsms.aidigitalsec.com/api/TimeTable/Id?TblId=${selectedTimeTableId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error("Failed to update subject. Please try again.");
      }

      setSnackbarMessage("Subject updated successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      handleEditDialogClose();
      await handleGetTimeTable();
    } catch (error) {
      setSnackbarMessage(error.message || "Error updating subject.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleDelete = async () => {
    try {
      const apiUrl = `https://wsms.aidigitalsec.com/api/TimeTable/Id?Id=${selectedTimeTableId}`;
      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Failed to delete subject. Please try again.");
      }

      setSnackbarMessage("Subject deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setOpenDeleteDialog(false);
      await handleGetTimeTable();
    } catch (error) {
      setSnackbarMessage(error.message || "Error deleting subject.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleGetTimeTable = async () => {
    if (!classId || !sectionId) {
      setSnackbarMessage("Please select both Class and Section");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/TimeTable/GetTimeTable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          classId,
          sectionId,
        }),
      });
      const data = await response.json();
      setTableData(data);
    } catch (error) {
      console.error("Error fetching timetable:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/ddlSection_clsId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            teacherId: sessionStorage.getItem("employeeId"),
            classId,
          }),
        }
      );
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const fetchDaysOfWeek = async () => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/TimeTable/GetDaysOfWeek",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      const data = await response.json();
      setDaysOfWeek(data);
    } catch (error) {
      console.error("Error fetching days of the week:", error);
    }
  };

  const fetchPeriods = async () => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/TimeTable/GetPeriod",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({}),
        }
      );
      const data = await response.json();
      setTableData(data);
    } catch (error) {
      console.error("Error fetching periods:", error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/AssignmentCreate/GetTeacher`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (response.ok) {
        setTeachers(Array.isArray(data) ? data : []);
      } else {
        console.error("Error fetching teachers:", data.message);
        setTeachers([]);
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setTeachers([]);
    }
  };

  const fetchSubjects = async (teacherId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/AssignmentCreate/Subjectddl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          classId,
          sectionId,
          teacherId
        }),
      });
      const data = await response.json();
      console.log("fetchSubjects response:", data); // Debug log
      if (response.ok && Array.isArray(data)) {
        setSubjects(data);
      } else {
        console.error("Error fetching subjects: Response is not an array or request failed", data);
        setSubjects([]);
        setSnackbarMessage("No subjects available for the selected teacher.");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
      setSnackbarMessage("Error fetching subjects.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchDaysOfWeek();
    fetchPeriods();
    fetchTeachers();
    Authorizer();
  }, []);

  const handleClassChange = (event) => {
    const selectedClassId = event.target.value;
    setClassId(selectedClassId);
    setSectionId("");
    setTableData([]);
    fetchSections(selectedClassId);
  };

  const handleSectionChange = (event) => {
    setSectionId(event.target.value);
    setTableData([]);
  };

  const handleTeacherChange = (event) => {
    const teacherId = event.target.value;
    setNewTeacher(teacherId);
    setNewSubject("");
    if (classId && sectionId && teacherId) {
      fetchSubjects(teacherId);
    } else {
      setSubjects([]);
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          backgroundColor: '#0B1F40',
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          Time Table
        </Typography>
      </Box>

      <Paper elevation={3} style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6, marginBottom: 6 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12}></Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Class"
              value={classId}
              onChange={handleClassChange}
              fullWidth
            >
              {classes.length > 0 ? (
                classes.map((cls) => (
                  <MenuItem key={cls.classId} value={cls.classId}>
                    {cls.className}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No Classes Available</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Section"
              value={sectionId}
              onChange={handleSectionChange}
              fullWidth
              disabled={sections.length === 0}
            >
              {sections.length > 0 ? (
                sections.map((section) => (
                  <MenuItem key={section.sectionId} value={section.sectionId}>
                    {section.sectionName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No Sections Available</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
            <Tooltip title={!canAdd && "You are not Authorized to Add"} arrow>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    marginRight: 6,
                    width: "100px",
                    height: "50px",
                    padding: "10px 0",
                    fontWeight: 'bold'
                  }}
                  onClick={handleAddTime}
                  disabled={!canAdd}
                >
                  Add Time
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={!classId && "Please Select Class" || !sectionId && "Please Select Section"} arrow>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  style={{
                    width: "140px",
                    height: "50px",
                    padding: "10px 0",
                    fontWeight: 'bold'
                  }}
                  onClick={handleGetTimeTable}
                  disabled={loading || !classId || !sectionId}
                >
                  {loading ? "Loading..." : "Get Time Table"}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} style={{ maxWidth: 910, width: "100%", borderRadius: 16, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)" }}>
        <Table size="medium" style={{ minWidth: 800 }}>
          <TableHead style={{ background: "linear-gradient(135deg, #3f51b5, #283593)" }}>
            <TableRow>
              <TableCell colSpan={2} style={{ color: "#ffffff", fontWeight: "bold", padding: "12px" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: "1.1em" }}>Time</span>
                  <span style={{ fontSize: "0.85em", fontWeight: "normal", color: "#bbdefb", whiteSpace: "nowrap" }}>
                    Start Time | End Time
                  </span>
                </div>
              </TableCell>
              {daysOfWeek.filter((day) => day.dayName.toLowerCase() !== "sunday").map((day) => (
                <TableCell key={day.dayId} style={{ color: "#ffffff", fontWeight: "bold", padding: "12px" }}>
                  {day.dayName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.length > 0 ? (
              tableData.reduce((rows, currentRow) => {
                const { startTime, endTime, subjectName, teacherName, teacherId, dayOfWeek, subjectId, timeTableId, roomNo, dayofWeekId } = currentRow;
                let row = rows.find(r => r.startTime === startTime && r.endTime === endTime);
                if (!row) {
                  row = { startTime, endTime, subjects: {}, teachers: {}, teacherIds: {}, subjectIds: {}, timeTableIds: {}, roomNos: {}, dayofWeekIds: {} };
                  rows.push(row);
                }
                row.subjects[dayOfWeek] = subjectName;
                row.teachers[dayOfWeek] = teacherName;
                row.teacherIds[dayOfWeek] = teacherId;
                row.subjectIds[dayOfWeek] = subjectId;
                row.timeTableIds[dayOfWeek] = timeTableId;
                row.roomNos[dayOfWeek] = roomNo;
                row.dayofWeekIds[dayOfWeek] = dayofWeekId;
                return rows;
              }, [])
              .sort((a, b) => {
  const parseTime = (t) => {
    const match = t.match(/(\d+):(\d+)(AM|PM)/i);
    if (!match) return 0;
    let [, h, m, period] = match;
    h = parseInt(h); m = parseInt(m);
    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
    if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  };
  return parseTime(a.startTime) - parseTime(b.startTime);
})
.map((row, index) => (
                <TableRow
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#e9f5ff",
                    cursor: "pointer",
                    transition: "background-color 0.3s",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  hover
                >
                  <TableCell style={{ borderRight: "1px solid #e0e0e0", padding: "12px", color: "#333" }}>{row.startTime}</TableCell>
                  <TableCell style={{ borderRight: "1px solid #e0e0e0", padding: "12px", color: "#333" }}>{row.endTime}</TableCell>
                  {daysOfWeek.filter((day) => day.dayName.toLowerCase() !== "sunday").map((day) => (
                  
<TableCell
  key={day.dayId}
  style={{
    borderRight: "1px solid #e0e0e0",
    padding: "12px",
    color: "#333",
    cursor: row.subjects[day.dayName] ? "pointer" : "default",
  }}
  onClick={
    row.subjects[day.dayName]
      ? () => handleSubjectCellClick(row, day.dayName, row.startTime, row.endTime, day.dayId)
      : undefined
  }
>

                      <div style={{ fontWeight: "bold" }}>{row.subjects[day.dayName] || "—"}</div>
                      <div style={{ fontSize: "0.8em", color: "#555", marginTop: "4px" }}>
                        ~ {row.teachers[day.dayName] || "—"}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={daysOfWeek.length + 2} style={{ textAlign: "center", padding: "24px", color: "#777" }}>No data available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={openEditDialog}
        onClose={handleEditDialogClose}
        aria-labelledby="edit-subject-teacher-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 24px" }}>
            <Typography
              id="edit-subject-teacher-title"
              variant="h6"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              {isEditing ? "Update Subject & Teacher" : "Edit Subject & Teacher"}
            </Typography>
          </Box>

          <Box sx={{ padding: "20px" }}>
            <TextField
              label="Class"
              fullWidth
              variant="outlined"
              value={editingRow?.className || ""}
              sx={{ marginBottom: "15px" }}
              disabled
            />
            <TextField
              label="Section"
              fullWidth
              variant="outlined"
              value={editingRow?.sectionName || ""}
              sx={{ marginBottom: "15px" }}
              disabled
            />
            <TextField
              select
              label="Teacher Name"
              fullWidth
              variant="outlined"
              value={newTeacher}
              onChange={handleTeacherChange}
              sx={{ marginBottom: "15px" }}
              disabled={!isEditing}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {teachers.map((teacher) => (
                <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                  {teacher.employeeName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Subject Name"
              fullWidth
              variant="outlined"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              sx={{ marginBottom: "15px" }}
              disabled={!isEditing || !newTeacher}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {Array.isArray(subjects) && subjects.length > 0 ? (
                subjects.map((subject) => (
                  <MenuItem key={subject.subjectId} value={subject.subjectId}>
                    {subject.subjectName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No Subjects Available</MenuItem>
              )}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px", gap: "10px" }}>
            <Button
              onClick={handleEditDialogClose}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            {isEditing ? (
              <Button
                onClick={handleUpdate}
                variant="contained"
                color="primary"
                disabled={!newTeacher || !newSubject}
              >
                Update
              </Button>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                variant="contained"
                color="primary"
              >
                Edit
              </Button>
            )}
            <Button
              onClick={handleDeleteClick}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-confirmation-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 24px" }}>
            <Typography
              id="delete-confirmation-title"
              variant="h6"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              Confirm Deletion ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: "20px", textAlign: "center" }}>
            <Typography variant="body1">
              Are you sure you want to delete this subject?
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px", gap: "10px" }}>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GetTimeTab;